import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export const LEAD_STATUSES = [
  "new", "contacted", "appointment_booked", "visited",
  "treatment_started", "follow_up", "converted", "not_interested", "closed",
];

/**
 * useLeads — encapsulates leads CRM state and mutations.
 * Owns: fetch, filter, update, delete. Refetches when status/query change.
 */
export function useLeads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", {
        params: { q: q || undefined, status },
      });
      setLeads(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => { load(); }, [status, load]);

  const updateLead = useCallback(async (id, updates) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, updates);
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      setSelected((cur) => (cur?.id === id ? data : cur));
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  const removeLead = useCallback(async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setSelected((cur) => (cur?.id === id ? null : cur));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  return {
    leads, loading, q, setQ, status, setStatus,
    selected, setSelected, load, updateLead, removeLead,
  };
}
